import { memo, useState, useRef, useCallback, type MouseEvent } from 'react'
import { Button, Tooltip, type ButtonSize } from '@highlight/ui'
import { Check, Copy, Link2 } from 'lucide-react'

interface CopyIconButtonProps {
  value: string
  icon: 'content' | 'link'
  ariaLabel: string
  stopPropagation?: boolean
  className?: string
  /** Unset follows the enclosing ButtonGroup, else Button's default. */
  size?: ButtonSize
  /** Fired only once the clipboard write succeeds, so telemetry can't count a copy that didn't happen. */
  onCopy?: () => void
}

export const CopyIconButton = memo(function CopyIconButton({
  value,
  icon,
  ariaLabel,
  stopPropagation,
  className,
  size,
  onCopy,
}: CopyIconButtonProps) {
  const [copied, setCopied] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleCopy = useCallback(
    async (e: MouseEvent) => {
      if (stopPropagation) e.stopPropagation()
      try {
        await navigator.clipboard.writeText(value)
        onCopy?.()
        if (timerRef.current) clearTimeout(timerRef.current)
        setCopied(true)
        timerRef.current = setTimeout(() => setCopied(false), 2000)
      } catch (err) {
        console.error('Failed to copy text: ', err)
      }
    },
    [value, stopPropagation, onCopy],
  )

  const Icon = icon === 'content' ? Copy : Link2
  const tooltip = copied ? 'Copied' : icon === 'content' ? 'Copy content' : 'Copy link'

  return (
    <Tooltip>
      <Tooltip.Trigger
        render={
          <Button
            shape="square"
            size={size}
            variant="outline"
            aria-label={ariaLabel}
            className={className}
            onClick={handleCopy}
          />
        }
      >
        {copied ? <Check className="size-3" /> : <Icon className="size-3" />}
      </Tooltip.Trigger>
      <Tooltip.Content>{tooltip}</Tooltip.Content>
    </Tooltip>
  )
})
